/**
 * Status-aware program actions menu — shared between the list page and the
 * edit shell header so the same menu groups appear in both contexts.
 *
 * Source: EU-151 (extracted from ProgramsPage.component.tsx).
 */

export type ProgramAction =
  | 'publish'
  | 'duplicate'
  | 'edit'
  | 'make-private'
  // EU-522 RN-01/RN-02 — "Tornar público" é a ação INVERSA de "Privar": vira um
  // programa já publicado como privado em público, sem passar pelo diálogo de
  // publicação (o consultor já escolheu a ação explicitamente). Distinta de
  // `publish`, que segue sendo a publicação inicial de um rascunho (o diálogo
  // oferece a escolha público/privado). RN-05 — só programa, nunca vaga.
  | 'make-public'
  // EU-408 — link privado do programa (página do app candidato): "Acessar"
  // abre em nova guia (prévia), "Copiar" copia para compartilhar com a empresa
  // contratante revisar antes do lançamento. Só aparece em programa publicado
  // como privado.
  | 'open-private-link'
  | 'copy-private-link'
  | 'end'
  | 'cancel'
  | 'discard'
  // Épico EU-561 + EU-654 — seção "Extrair bases": baixa o .xlsx com a base
  // de respostas de TODOS os inscritos do programa (todas as vagas). EU-654
  // REMOVEU 'export-form' (a Completa o substitui) e acrescentou a Completa e
  // o Raio-X de Carreira; os labels são responsabilidade do getExportGroup.
  | 'export-complete'
  // DEV-498 — exportação em massa (assíncrona, resultado por e-mail): entram
  // logo depois de "Completa" e são ofertadas em TODO programa (AC-01..04).
  | 'export-bulk-resumes'
  | 'export-bulk-attestations'
  | 'export-fit-cultural'
  | 'export-learning-map'
  | 'export-career-xray';

export type ProgramStatus = 'ativa' | 'ativo-privado' | 'rascunho' | 'encerrado' | 'cancelado';

/**
 * EU-151 RN-01 — only the 3 mutable statuses can reach the edit form. Used by
 * the row title click in the listing so the consultor can't slip past the
 * action-menu guard (which already hides "Editar" for encerrado/cancelado).
 * Core would reject a PATCH on a closed program anyway; this gates the UX
 * before the round-trip so the consultor doesn't see a half-loaded edit form.
 */
export function isProgramEditable(status: ProgramStatus): boolean {
  return status === 'rascunho' || status === 'ativa' || status === 'ativo-privado';
}

export interface MenuOption {
  action: ProgramAction;
  label: string;
  destructive?: boolean;
}

/**
 * Status-specific contextual menus per Figma `Home / Behavior` (13070:13587).
 * Order is load-bearing — the design lists options top-down.
 */
export function getMenuGroupsForStatus(status: ProgramStatus): MenuOption[][] {
  if (status === 'rascunho') {
    // RN-07 (EU-149) — Rascunho expõe: Publicar, Duplicar, Editar, Encerrar
    // e Cancelar. "Encerrar" estava ausente na implementação original e a
    // QA pegou. Mantém a ordem do AC literal do card.
    return [
      [
        { action: 'publish', label: 'Publicar' },
        { action: 'duplicate', label: 'Duplicar' },
        { action: 'edit', label: 'Editar' },
        { action: 'end', label: 'Encerrar' },
      ],
      [{ action: 'cancel', label: 'Cancelar', destructive: true }],
    ];
  }
  if (status === 'ativo-privado') {
    // EU-522 RN-01/RN-03 — o toggle de visibilidade aqui é "Tornar público"
    // (inverso do "Privar" do status "ativa"). Antes o menu expunha "Publicar",
    // que abria o diálogo de publicação de rascunho — o programa já está
    // publicado, só falta trocar a visibilidade.
    // RN-04 — os itens de link privado (EU-408) FAZEM PARTE do conjunto
    // esperado deste menu; não são divergência de Figma.
    return [
      [
        { action: 'make-public', label: 'Tornar público' },
        { action: 'open-private-link', label: 'Acessar link privado' },
        { action: 'copy-private-link', label: 'Copiar link privado' },
        { action: 'edit', label: 'Editar' },
        { action: 'duplicate', label: 'Duplicar' },
        { action: 'end', label: 'Encerrar' },
      ],
      [{ action: 'cancel', label: 'Cancelar', destructive: true }],
    ];
  }
  if (status === 'ativa') {
    // EU-428 — ordem do frame Behavior (13070:13587): a ação de visibilidade
    // abre o grupo, depois Editar, Duplicar e Encerrar. Espelha o menu de
    // "Ativo e priv." (lá a visibilidade é "Tornar público"), então as duas
    // variantes do programa publicado leem igual.
    return [
      [
        { action: 'make-private', label: 'Privar' },
        { action: 'edit', label: 'Editar' },
        { action: 'duplicate', label: 'Duplicar' },
        { action: 'end', label: 'Encerrar' },
      ],
      [{ action: 'cancel', label: 'Cancelar', destructive: true }],
    ];
  }
  // encerrado / cancelado — only Duplicar per design.
  return [[{ action: 'duplicate', label: 'Duplicar' }]];
}

/**
 * Menu options shown when editing a copy (isCopy = true).
 * Figma 2: only "Descartar cópia" and "Cancelar".
 */
export function getCopyMenuGroups(): MenuOption[][] {
  return [
    [{ action: 'discard', label: 'Descartar cópia', destructive: true }],
    [{ action: 'cancel', label: 'Cancelar' }],
  ];
}

/**
 * Seção "Extrair base" do menu do programa (épico EU-561, Figma 15411-5106).
 *
 * Independente do status: extrair a base de um programa já encerrado é
 * justamente quando o consultor analisa e ranqueia os candidatos — por isso
 * não passa por `getMenuGroupsForStatus`, que responde outra pergunta ("o que
 * dá para gerenciar neste status").
 *
 * A ordem é a do Figma. "Fazer ranking" e "Conferir candidatos" aparecem no
 * mesmo grupo do desenho mas não têm história no épico — ficam de fora.
 */
/** EU-654 — chaves de wire dos tipos de base ofertáveis (ver plano 064). */
export type ProgramBaseType =
  | 'complete'
  | 'bulk-resumes'
  | 'bulk-attestations'
  | 'fit-cultural'
  | 'learning-map'
  | 'career-xray';

const EXPORT_OPTION_BY_TYPE: Record<ProgramBaseType, MenuOption> = {
  complete: { action: 'export-complete', label: 'Completa' },
  'bulk-resumes': { action: 'export-bulk-resumes', label: 'Currículos em massa' },
  'bulk-attestations': { action: 'export-bulk-attestations', label: 'Laudos em massa' },
  'fit-cultural': { action: 'export-fit-cultural', label: 'FIT Cultural' },
  'learning-map': { action: 'export-learning-map', label: 'Raio-X Comportamental' },
  'career-xray': { action: 'export-career-xray', label: 'Raio-X de Carreira' },
};

/**
 * DEV-498 — ação do menu → tipo da exportação em massa; `null` para as demais.
 * Separado de `baseTypeForExportAction` de propósito: em massa NÃO baixa
 * arquivo (hook e toasts próprios).
 */
export function bulkExportTypeForAction(action: ProgramAction): 'resumes' | 'attestations' | null {
  switch (action) {
    case 'export-bulk-resumes':
      return 'resumes';
    case 'export-bulk-attestations':
      return 'attestations';
    default:
      return null;
  }
}

/** Ação do menu → tipo de base (download); `null` para ações que não são de download. */
export function baseTypeForExportAction(action: ProgramAction): ProgramBaseType | null {
  switch (action) {
    case 'export-complete':
      return 'complete';
    case 'export-fit-cultural':
      return 'fit-cultural';
    case 'export-learning-map':
      return 'learning-map';
    case 'export-career-xray':
      return 'career-xray';
    default:
      return null;
  }
}

/**
 * EU-654 (supersede a lista fixa do EU-428/EU-561): a seção "Extrair bases" é
 * DINÂMICA — renderiza exatamente os tipos que o BACKEND ofertou para o
 * programa (RN-02/03), na ordem recebida (o Core já manda a canônica:
 * Completa → FIT Cultural → Raio-X Comportamental → Raio-X de Carreira).
 * "Formulário" e "Mapa de Aprendizagem" deixaram de existir como rótulos.
 * `undefined` = disponibilidade ainda carregando (o caller mostra o estado).
 */
export function getExportGroup(types: ProgramBaseType[] | undefined): MenuOption[] {
  if (!types) return [];
  return types.map((type) => EXPORT_OPTION_BY_TYPE[type]);
}
